import { parseJSON } from './json';
import { API_ENDPOINT } from '../constants';

/**
* Data access + small domain helpers for the external API "insights" endpoint
* (VD-725 follow-up): duplicates, bursts, a send-time heatmap, slow requests,
* query/path parameter breakdowns and top errors.
*/

export const INSIGHTS_DEFAULTS = {
    duplicateWindowSec: 60,
    burstPerMinute: 60,
    slowLimit: 10,
    slowThresholdMs: 2000,
    tz: 'Europe/Stockholm',
};

export const DURATION_BUCKETS = [0, 100, 250, 500, 1000, 2000, 5000, 10000, 30000];

const unitOf = (ms) => ms < 1000 ? 'ms' : 's';
const numOf = (ms) => ms < 1000 ? ms : Number((ms / 1000).toFixed(1).replace(/\.0$/, ''));
const fmtBound = (ms) => `${numOf(ms)} ${unitOf(ms)}`;

/** Human label for a durationHistogram bucket's `fromMs`, e.g. "< 100 ms", "100-250 ms", "500 ms-1 s", "30 s+" */
export const durationBucketLabel = (fromMs) => {
    let idx = DURATION_BUCKETS.indexOf(Number(fromMs));

    if (idx === -1) {
        return fmtBound(fromMs);
    }

    if (idx === 0) {
        return `< ${fmtBound(DURATION_BUCKETS[1])}`;
    }

    if (idx === DURATION_BUCKETS.length - 1) {
        return `${fmtBound(fromMs)}+`;
    }

    let to = DURATION_BUCKETS[idx + 1];

    // same unit on both ends -> state it once ("100-250 ms"), otherwise keep both ("500 ms-1 s")
    return unitOf(fromMs) === unitOf(to)
        ? `${numOf(fromMs)}-${numOf(to)} ${unitOf(to)}`
        : `${fmtBound(fromMs)}-${fmtBound(to)}`;
};

const buildQuery = (filters) => {
    let qs = new URLSearchParams();

    if (filters.from) qs.set('from', filters.from);
    if (filters.to) qs.set('to', filters.to);
    if (filters.clientId) qs.set('clientId', filters.clientId);
    if (filters.apiKeyId) qs.set('apiKeyId', filters.apiKeyId);
    if (filters.route) qs.set('route', filters.route);
    if (filters.method) qs.set('method', filters.method);

    if (filters.duplicateWindowSec) qs.set('duplicateWindowSec', filters.duplicateWindowSec);
    if (filters.burstPerMinute) qs.set('burstPerMinute', filters.burstPerMinute);
    if (filters.slowLimit) qs.set('slowLimit', filters.slowLimit);
    if (filters.slowThresholdMs) qs.set('slowThresholdMs', filters.slowThresholdMs);
    if (filters.tz) qs.set('tz', filters.tz);

    return qs.toString();
};

/** @returns {Promise<{data: object|null, error: string|null}>} */
export const fetchExternalApiInsights = (filters) => {
    let qs = buildQuery(filters || {});

    return fetch(`${API_ENDPOINT}/data/external-api/insights${qs ? '?' + qs : ''}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
    }).then(parseJSON).then(({ result, status }) => {
        if (status >= 200 && status < 300) {
            return { data: result, error: null };
        }

        return { data: null, error: result && result.error ? result.error : 'Failed to load insights' };
    });
};
